import { useCallback } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getWorkingProjectSessions,
  selectIsLoadingWorkingSession,
  selectProjectSettings,
  selectWorkingProjectDefaultLanguage,
  selectWorkingProjectLanguages,
  selectWorkingProjectType,
  selectWorkingSession,
} from '@/store/slices/project/selectors';
import { getUser } from '@/store/slices/user/selectors';
import { Case, SwitchCase } from '@ui/switch-case/switch-case';
import EditSessionImage from '../../components/edit-session-image/edit-session-image';
import { EventType } from '@/types/projects.types';
import EditProjectCategory from '../../components/edit-project-category/edit-project-category';
import EditProjectSeries from '../../components/edit-project-series/edit-project-series';
import EditProjectTitle from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/edit-project-title/edit-project-title';
import Container from '@ui/container/container';
import SessionDateInfo from '../../components/session-date-info/session-date-info';
import SessionVodSection from '../../components/session-date-info/session-vod-section';
import EditSessionDescription from '../../components/edit-session-description/edit-session-description';
import EditTags from '../../components/edit-tags/edit-tags';
import EditSessionLanguage from '../../components/edit-session-language/edit-session-language';
import EventStats from '../../components/stats-row/event-stats/event-stats';
import TownhallStats from '../../components/stats-row/townhall-stats/townhall-stats';
import WebinarStats from '../../components/stats-row/webinar-stats/webinar-stats';
import { EBroadcastTypes, SessionTypesEnum } from '@/types/session.types';
import BroadcastTypePicker from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/broadcast-type-picker';
import handleBroadcastTypeChange from '../../components/session-date-info/helpers/handle-broadcast-type-change';
import getSelectedBroadcastType from '../../components/session-date-info/helpers/get-selected-broadcast-type';
import DestinationSelect from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/destination-select/destination-select';
import TimezoneSelect from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/dates/timezone-select';
import usePersistedTimezone from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/dates/use-persisted-timezone';
import OptionalComponent from '@ui/optional-component/optional-component';
import useResourcePool, { ResourcePoolContext } from '@/utils/greenroom/use-resource-pool';
import ActivityIndicator from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/activity-indicator/activity-indicator';
import useLoadSession from '@/connection/hooks/use-load-session';
import Chip from '@styles/design-system/components/chips/chip';
import { useLoadCurrentSeries } from '../../components/edit-project-series/use-load-current-series';
import { selectCurrentSeries, selectTopDrawerOpen } from '@/store/slices/series/selectors';
import { closeTopDrawer, openTopDrawer } from '@/store/slices/series';
import CastAndCrewSelectWithInvites from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/cast-and-crew/cast-and-crew-select-with-invites/cast-and-crew-select-with-invites';
import Engagement from '../../components/engagement-row/engagement';
import EditSessionCaptions from '../../components/edit-session-captions/edit-session-captions';
import ProjectOverviewSegments from '../../../segments/project-overview-segments';
import GreenroomShotsRow from '../../components/greenroom-shots-row/greenroom-shots-row';
import useSegmentsFeatureFlag from '../../../segments/use-segments-feature-flag';
import ProjectImageSkeletonLoader from '@ui/skeleton-loader/custom/project-image-skeleton-loader';
import Text from '@ui/text/text/text';
import ProjectColorIcon from '@ui/icons/icon/project-color-icon';
import { ICONS } from '@ui/icons/types';
import ProjectOverviewPopoverActions from '../../components/project-header-row/project-overview-popover-actions';

const OverviewSingleSession = () => {
  const projectType = useAppSelector(selectWorkingProjectType);
  const sessions = useAppSelector(getWorkingProjectSessions);
  const session = useAppSelector(selectWorkingSession);
  const resourcePool = useResourcePool();
  const user = useAppSelector(getUser);
  const projectLanguages = useAppSelector(selectWorkingProjectLanguages);
  const projectDefaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const workingProjectSettings = useAppSelector(selectProjectSettings);
  const currentSeries = useAppSelector(selectCurrentSeries);
  const topDrawerOpen = useAppSelector(selectTopDrawerOpen);
  const isLoadingWorkingSession = useAppSelector(selectIsLoadingWorkingSession);
  const dispatch = useAppDispatch();

  const params = useParams();

  const projectUuid = params?.projectUuid;
  const language = params?.language || 'en';

  const isOnDemand = session?.session_type === SessionTypesEnum.onDemand;
  const isSingleSession = sessions?.length === 1;
  const sessionUuid = sessions?.[0]?.uuid;
  const sessionId = sessions?.[0]?.session;

  useLoadSession(sessionId);
  useLoadCurrentSeries();

  const { segmentsEnabled, loading: isLoadingSegmentsFeatureFlag } = useSegmentsFeatureFlag();
  const [selectedTimezone, setSelectedTimezone] = usePersistedTimezone(projectUuid);
  const greenroomRowHeight = segmentsEnabled ? '140px' : '235px';

  const toggleTopDrawer = useCallback(() => {
    if (!topDrawerOpen) {
      dispatch(openTopDrawer());
    } else {
      dispatch(closeTopDrawer());
    }
  }, [topDrawerOpen, dispatch]);

  const getTitlePlaceholder = useCallback(() => {
    if (projectType === EventType.Webinar) {
      return 'Give your webinar a name';
    }
    return 'Give your project a name';
  }, [projectType]);

  if (!projectUuid) {
    return <Navigate to="/choose" />;
  }
  if (!isSingleSession) {
    <Navigate to={`/project/edit/${projectUuid}/${language}/${sessionUuid}`} />;
  }

  return (
    <div data-component="OverviewSingleSession">
      <div className="flex-column gap-10">
        <Container
          padding="0 var(--main-content-horizontal-padding) 10px var(--main-content-horizontal-padding)"
          className="flex flex-1 jc-space-between"
        >
          <Container className="flex flex-1">
            {!!currentSeries && !!session && (
              <Container className="text-nowrap margin-r-8">
                <Chip
                  size={32}
                  onClick={toggleTopDrawer}
                  label={currentSeries.title}
                  icon={ICONS.SELECT_DROPDOWN_CARET}
                  rotateIcon={topDrawerOpen ? '0deg' : '180deg'}
                  selected={topDrawerOpen}
                  iconPlacement="right"
                  iconColor="var(--global-ico-text-3)"
                  selectProps={{
                    style: {
                      padding: '4px 12px',
                      background: 'var(--global-cont-2)',
                      borderColor: 'var(--global-cont-3)',
                    },
                  }}
                />
              </Container>
            )}
            <EditProjectTitle
              syncSessionUuid={sessionUuid}
              sessionDefaultLanguage={session?.default_language}
              placeholder={getTitlePlaceholder()}
            />
          </Container>
          <Container
            className="flex"
            skLoader={{
              height: 32,
              width: 120,
              radius: 8,
              loading: isLoadingWorkingSession,
              design: 'GlobalCont1',
            }}
          >
            <ActivityIndicator />
            <ProjectOverviewPopoverActions />
          </Container>
        </Container>
        <Container
          padding="0 var(--main-content-horizontal-padding)"
          className="flex jc-flex-start ai-flex-start gap-24 margin-b-16"
        >
          <Container width="392px" className="flex-column gap-16">
            {/* syncProjectImage = if single session we need to also update the project image when updating the session image */}

            <Container style={{ position: 'relative' }}>
              <EditSessionImage />
              <ProjectImageSkeletonLoader loading={isLoadingWorkingSession} />
            </Container>

            <Container
              skLoader={{
                height: '100%',
                width: 392,
                radius: 8,
                loading: isLoadingWorkingSession,
                design: 'GlobalCont1',
              }}
            >
              <SwitchCase input={projectType}>
                <Case value={EventType.Event}>
                  <EventStats />
                </Case>
                <Case value={EventType.Webinar}>
                  <WebinarStats />
                </Case>
                <Case value={[EventType.Allhands, EventType.Townhall]}>
                  <TownhallStats />
                </Case>
              </SwitchCase>
            </Container>

            <Container className="flex fd-row gap-16">
              <Container width="95px">
                <EditSessionCaptions />
              </Container>
              <Container width="278px">
                <EditSessionLanguage sessionUuid={sessionUuid} />
              </Container>
            </Container>
          </Container>

          <Container
            className="flex-column flex-1 gap-20"
            skLoader={{
              height: 442,
              width: 576,
              radius: 16,
              loading: isLoadingWorkingSession,
              design: 'GlobalCont1',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
              {isOnDemand ? (
                <>
                  <div style={{ gridColumn: 'span 2' }}>
                    <SessionVodSection />
                  </div>
                  <div className="margin-t-24 flex ai-center">
                    <Container
                      className="flex ai-center gap-8 padding-h-12 height-32"
                      border="2px solid var(--local-chip-input-default-outline)"
                      borderRadius="100px"
                      background="var(--local-chip-input-default-bg)"
                      width="fit-content"
                    >
                      <ProjectColorIcon name={ICONS.ON_DEMAND} size={16} />
                      <Text design="GlobalIcoText1" className="S3" whiteSpace="nowrap">
                        On Demand
                      </Text>
                    </Container>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ gridColumn: 'span 2' }}>
                    <SessionDateInfo syncProjectDates selectedTimezone={selectedTimezone} />
                  </div>
                  <div>
                    <TimezoneSelect selectedTimezone={selectedTimezone} onChange={setSelectedTimezone} />
                  </div>
                  <div>
                    <BroadcastTypePicker
                      selected={getSelectedBroadcastType(session)}
                      onChange={(type) =>
                        handleBroadcastTypeChange({
                          type,
                          sessions,
                          session,
                          user,
                          projectLanguages,
                          projectDefaultLanguage,
                          dispatch,
                          workingProjectSettings,
                        })
                      }
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <DestinationSelect />
                  </div>
                </>
              )}
              <EditProjectSeries />
              <EditProjectCategory syncSessionUuid={sessionUuid} />
              <EditTags sessionUuid={sessionUuid} />
              <div style={{ gridColumn: 'span 3' }}>
                <EditSessionDescription sessionUuid={sessionUuid} syncProjectDescription />
              </div>
              <div style={{ gridColumn: 'span 3' }}>
                <CastAndCrewSelectWithInvites sessionUuid={sessionUuid} design="flat" />
              </div>
            </div>
          </Container>
        </Container>

        <Container
          /*
            since we do not know if showboards exist until after the session loads, the default will be to
            show a loading indicator for the show board until sesssion loading is finished or we know the
          */
          height={
            isLoadingWorkingSession || session?.broadcast_type === EBroadcastTypes.greenroom ? greenroomRowHeight : '0'
          }
          className={session?.broadcast_type === EBroadcastTypes.greenroom ? '' : 'overflow-hidden pointer-events-none'}
          style={{ transition: 'height 0.3s ease' }}
          skLoader={{
            height: greenroomRowHeight,
            width: 'var(--main-content-max-width, 992px)',
            radius: 16,
            loading: isLoadingWorkingSession || isLoadingSegmentsFeatureFlag,
            design: 'GlobalCont1',
          }}
        >
          <OptionalComponent display={session?.broadcast_type === EBroadcastTypes.greenroom}>
            <ResourcePoolContext.Provider value={resourcePool}>
              {segmentsEnabled ? <ProjectOverviewSegments /> : <GreenroomShotsRow sessionUuid={sessionUuid} />}
            </ResourcePoolContext.Provider>
          </OptionalComponent>
        </Container>

        <Container
          skLoader={{
            height: 179,
            width: 'var(--main-content-max-width, 992px)',
            radius: 16,
            loading: isLoadingWorkingSession,
            design: 'GlobalCont1',
          }}
        >
          <Engagement />
        </Container>
      </div>
    </div>
  );
};

export default OverviewSingleSession;
